import React, {useMemo} from 'react';
import {Platform, Pressable, View, Text} from 'react-native';
import {CheckboxProps} from '../../../../shared/components/ui/Checkbox/CheckboxProps';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {createCheckboxStyles} from '../../../../shared/components/ui/Checkbox/Checkbox.styles';
import TickMarkIcon from '@shared/assets/icons/TickMarck';

/**
 * Checkbox component with full Figma interaction-state support.
 *
 * Visual state priority (highest → lowest):
 *   checked+disabled → disabled → checked → pressed → normal
 *
 * @param isChecked   - controlled checked state
 * @param onPress     - toggling callback (suppressed when disabled)
 * @param label       - optional text label rendered beside the box
 * @param disabled    - renders the disabled visual state; suppresses onPress
 */
const Checkbox = ({
  isChecked,
  onPress,
  label,
  containerStyle,
  testID,
  customMainContainerStyle,
  disabled = false,
  ...rest
}: CheckboxProps) => {
  const theme = useScreenTheme('checkbox');
  const styles = useMemo(() => createCheckboxStyles(theme), [theme]);

  // Web-specific ARIA props so screen readers correctly announce checked state.
  // React Native's accessibilityState is not reliably mapped to aria-checked on web.
  const webAriaProps =
    Platform.OS === 'web'
      ? {
          role: 'checkbox' as const,
          'aria-checked': isChecked,
          'aria-disabled': disabled,
          'aria-label': label,
        }
      : {};

  return (
    <Pressable
      {...rest}
      {...webAriaProps}
      onPress={disabled ? undefined : onPress}
      style={styles.container}
      testID={testID}
      disabled={disabled}
      accessibilityRole="checkbox"
      accessibilityState={{checked: isChecked, disabled}}
      accessibilityLabel={label}>
      {({pressed}) => (
        <>
          <View style={[styles.checkboxMainContainer, customMainContainerStyle]}>
            <View
              style={[
                // Base style — always applied
                styles.checkboxBase,
                // Checked overrides base
                isChecked && styles.checkboxChecked,
                // Pressed (only when unchecked and enabled)
                pressed && !isChecked && !disabled && styles.checkboxPressed,
                // Disabled (unchecked)
                disabled && !isChecked && styles.checkboxDisabled,
                // Checked + Disabled (highest priority)
                disabled && isChecked && styles.checkboxCheckedDisabled,
                // Caller-provided style override
                containerStyle,
              ]}>
              {isChecked && <TickMarkIcon color={theme.tickMarkIconColor} />}
            </View>
          </View>
          {label && (
            <Text style={[styles.label, disabled && styles.labelDisabled]}>
              {label}
            </Text>
          )}
        </>
      )}
    </Pressable>
  );
};

export default Checkbox;
